import { z } from 'zod';
import { languageSchema } from '../../common/language.schema';

/**
 * Communication channel used for outreach / by the AI agent.
 * Ordered from most-preferred (whatsapp is default in KSA) to
 * least-preferred (voice — expensive, intrusive).
 */
export const outreachChannelSchema = z.enum(['whatsapp', 'sms', 'push', 'email', 'voice']);
export type OutreachChannel = z.infer<typeof outreachChannelSchema>;

/**
 * Policy set for a user's channels. Explicit user preferences —
 * NOT empirical inferences. See `docs/ai/user-signals.md`.
 *
 * `preferred[]` is the ordered list the outreach decision engine
 * walks; `blocked[]` is a hard veto that overrides everything else
 * including `preferred[]`.
 */
export const userMetaChannelsPolicySchema = z.object({
  /** Ordered list of preferred channels. First matching = winner. */
  preferred: z.array(outreachChannelSchema).default([]),
  /** Hard veto — the agent must never contact via these channels. */
  blocked: z.array(outreachChannelSchema).default([]),
});
export type UserMetaChannelsPolicy = z.infer<typeof userMetaChannelsPolicySchema>;

/**
 * "Do not disturb" window in the user's local timezone. Read by
 * the outreach decision engine before every proactive message.
 *
 * The window can wrap midnight — `start: "22:00"`, `end: "07:00"`
 * is 9 hours long, not 15.
 */
export const userMetaChannelsDNDSchema = z.object({
  /** 24-hour local time "HH:mm" (e.g. "22:00"). */
  start: z.string().regex(/^\d{2}:\d{2}$/),
  /** 24-hour local time "HH:mm" (e.g. "07:00"). Wraps midnight when < start. */
  end: z.string().regex(/^\d{2}:\d{2}$/),
});
export type UserMetaChannelsDND = z.infer<typeof userMetaChannelsDNDSchema>;

/**
 * Empirical WhatsApp reachability. Populated by the WhatsApp
 * adapter based on Meta webhook receipts, NOT set by the user.
 */
export const userMetaChannelsWhatsAppSchema = z.object({
  /**
   * `true` when we have any evidence the number is reachable on
   * WhatsApp (delivered receipt or inbound message). `false` when
   * a send returned an "invalid recipient" error. `undefined` when
   * we've never tried.
   */
  isReachable: z.boolean().optional(),
  /** Timestamp of the most recent delivery / receipt confirmation. */
  lastDeliveryAt: z.date().optional(),
  /** Timestamp of the most recent inbound message. */
  lastInboundAt: z.date().optional(),
  /** Most recent send failure reason (Meta error slug). */
  lastFailureReason: z.string().trim().optional(),
});
export type UserMetaChannelsWhatsApp = z.infer<typeof userMetaChannelsWhatsAppSchema>;

/**
 * Empirical push notification reachability. Populated by the
 * OneSignal / OS-provider adapter.
 */
export const userMetaChannelsPushSchema = z.object({
  /** `true` when we have at least one active `User.devices[].token`. */
  hasActiveToken: z.boolean().optional(),
  /** Timestamp of the most recent successful push delivery. */
  lastDeliveryAt: z.date().optional(),
  /** Timestamp of the most recent tap / open event on a push. */
  lastOpenedAt: z.date().optional(),
  /**
   * Whether OneSignal reports the user as opted out. Set to `true`
   * when the platform tells us the user disabled push at the OS
   * level or in-app.
   */
  optedOut: z.boolean().optional(),
});
export type UserMetaChannelsPush = z.infer<typeof userMetaChannelsPushSchema>;

/**
 * Empirical voice-call reachability. Populated by the voice
 * adapter (Twilio / native dialer callbacks).
 */
export const userMetaChannelsVoiceSchema = z.object({
  /** Timestamp of the most recent connected outbound call. */
  lastAnsweredAt: z.date().optional(),
  /** Timestamp of the most recent unanswered outbound call. */
  lastMissedAt: z.date().optional(),
  /** Number of consecutive misses since last connect. */
  consecutiveMisses: z.number().int().nonnegative().optional(),
});
export type UserMetaChannelsVoice = z.infer<typeof userMetaChannelsVoiceSchema>;

/**
 * Full channels block. Explicit policy (`policy`, `outreachOptOut`,
 * `dnd`, `outreachLanguage`) is set by the user or by staff.
 * Empirical blocks (`whatsapp`, `push`, `voice`) are learned by the
 * adapters. See `docs/ai/user-signals.md`.
 */
export const userMetaChannelsSchema = z.object({
  policy: userMetaChannelsPolicySchema.optional(),
  /**
   * Hard opt-out from ALL outreach. `true` overrides `policy`,
   * `dnd`, empirical reachability — nothing goes out.
   */
  outreachOptOut: z.boolean().optional(),
  dnd: userMetaChannelsDNDSchema.optional(),
  /**
   * Language to use for proactive outreach. Falls back to
   * `User.preferredLanguage` when unset.
   */
  outreachLanguage: languageSchema.optional(),
  whatsapp: userMetaChannelsWhatsAppSchema.optional(),
  push: userMetaChannelsPushSchema.optional(),
  voice: userMetaChannelsVoiceSchema.optional(),
});
export type UserMetaChannels = z.infer<typeof userMetaChannelsSchema>;
